/* Core & libraries */
import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useLabelService } from "../../services/labelService";
import { colors } from "../../constants/colors";
import { useSpinnersContext } from "../../contexts/spinnersContext";

/* Components */
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import UCOutsideClickHandler from "../ucOusideClickHandler/UCOutsideClickHandler";
import EditLabelModal from "../modals/editLabelModal";
import DeleteActionModal from "../modals/deleteActionModal";
import Spinner from "../spinners/buttonSpinner";
import { ReactComponent as AsLabelPlus } from '../../resources/images/as-label-plus.svg';
import { ReactComponent as AsLabelEdit } from '../../resources/images/as-label-edit.svg';
import { ReactComponent as AsLabelChecked } from '../../resources/images/as-label-check.svg';
import { ReactComponent as LabelIcon } from '../../resources/images/as-label.svg';

/* Styles */
import styles from "./styles.js"

const LabelDropdown = ({ activeLabel, setSelectedLabelCallback, userEntityId = null, placement = "bottom-start" }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();
  const inputRef = useRef(null);
  const skipActiveLabelSyncRef = useRef(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [labelToEdit, setLabelToEdit] = useState(null);
  const [labels, setLabels] = useState([]);
  
  const { createLabel, updateLabel, deleteLabel, getLabels, associateLabel, unassignLabel } = useLabelService();
  const spinner = useSpinnersContext();
  
  useEffect(() => {
    if (!skipActiveLabelSyncRef.current) {
      setSelectedLabel(activeLabel);
    }
    skipActiveLabelSyncRef.current = false;
  }, [activeLabel]);
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 100)
    }
  }, [isOpen]);

  const getLabelsAction = async () => {
    const getLabelsResponse = await getLabels(debouncedValue);
    if (getLabelsResponse) {
      setLabels(getLabelsResponse);
    }
  }

  useEffect(() => {
    if (isOpen) {
      (async () => {
        await getLabelsAction(debouncedValue)
      })();
    }
  }, [isOpen, debouncedValue]);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500);
    
    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);
  
  const isLabelsSameAsInput = (inputValue) => {
    if (inputValue) {
      return labels.some(item => item.label.toLowerCase() === inputValue.toLowerCase())
    }
  }
  
  const handleOpenDropdown = (e) => {
    e.preventDefault();
    setIsOpen(true);
  };
  
  const handleCloseDropDown = () => {
    setIsOpen(false);
    setInputValue("");
    setDebouncedValue("")
  }
  
  
  const handleSelect = async (label) => {
    let newLabel = label;
    const isSameLabel = label?.labelId === selectedLabel?.labelId;
    
    if (userEntityId) {
      const payload = {
        labelId: label?.labelId,
        userEntityId: userEntityId,
      }
      if (isSameLabel) {
        const response = await unassignLabel(payload);
        if (response) {
          newLabel = null;
          skipActiveLabelSyncRef.current = true;
        }
      } else {
        const response = await associateLabel(payload);
        if (response) {
          newLabel = response?.label;
          skipActiveLabelSyncRef.current = true;
        }
      }
    } else {
      newLabel = isSameLabel ? null : label;
      skipActiveLabelSyncRef.current = true;
    }
    
    setSelectedLabel(newLabel);
    if (setSelectedLabelCallback) {
      setSelectedLabelCallback(newLabel);
    }
    setIsOpen(false);
  };
  const handleOpenEditModal = (e, label) => {
    e.stopPropagation();
    setLabelToEdit(label);
    setShowEditModal(true);
  }
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  }
  
  const handleCreateLabel = async () => {
    if (!isLabelsSameAsInput(inputValue)) {
      const payload = {
        labelId: "",
        label: inputValue
      }
      let createLabelResponse
      if (userEntityId) {
        createLabelResponse = await associateLabel({...payload, userEntityId});
      } else {
        createLabelResponse = await createLabel(payload);
      }

      if (createLabelResponse) {
        setInputValue("");
        setSelectedLabel(createLabelResponse?.label);
        if (setSelectedLabelCallback) {
          setSelectedLabelCallback(createLabelResponse?.label);
        }
        setIsOpen(false)
      }
    }
  }
  
  const handleUpdateLabel = async (label) => {
    if (label?.labelId === selectedLabel?.labelId) {
      setSelectedLabel(label);
    }
    if (setSelectedLabelCallback) {
      setSelectedLabelCallback(label);
    }
    await getLabelsAction(inputValue);
  }
  
  const handleDeleteLabel = async () => {
    const deleteLabelResponse = await deleteLabel(labelToEdit?.labelId);
    if (deleteLabelResponse) {
      if (labelToEdit?.labelId === selectedLabel.labelId) {
        setSelectedLabel(null)
      }
      if (setSelectedLabelCallback) {
        setSelectedLabelCallback(null);
      }
      await getLabelsAction(inputValue);
      setShowDeleteModal(false);
    }
  }
  
  const disableCreate = (inputValue) => {
    if (spinner.getLabels) {
      return true;
    }
    if (inputValue) {
      return isLabelsSameAsInput(inputValue);
    }
  }
  
  const renderSelectedLabel = () => {
    return (
      selectedLabel ?
        <div
          className={classnames(
            classes.selectedLabelButton,
            !userEntityId && classes.selectedLabelButtonRounded
          )}
          onClick={handleOpenDropdown}
        >
          {selectedLabel?.label}
        </div> :
        <div
          className={classnames(
            classes.addLabelButton,
            !userEntityId && classes.addLabelButtonRounded
          )}
          onClick={handleOpenDropdown}
        >
          <FormattedMessage id="guestLabel.addLabel"/>
        </div>
    )
  }
  
  const getLabelsContent = () => {
    if (spinner.getLabels) {
      return (
        <div className={classes.spinnerContainer}>
          <Spinner size={20} color={colors.blueAS}/>
        </div>
      )
    } else if (labels?.length > 0) {
      return (
        <>
          <div className={classes.labelsContainer}>
            {labels.map((label, index) => {
              const isSelected = label?.labelId === selectedLabel?.labelId;
              const isHovered = index === hoveredIndex;
              
              return (
                <div
                  key={index}
                  className={classnames(classes.label, isSelected && classes.labelSelected)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => handleSelect(label)}
                >
                  <span className={classes.labelName}>{label?.label}</span>
                  {isSelected && !isHovered &&
                    <div className={classes.icon}><AsLabelChecked className="check-icon"/></div>
                  }
                  {isHovered && (
                    <div className={classes.icon} onClick={(e) => handleOpenEditModal(e, label)}>
                      <AsLabelEdit />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {inputValue &&
            <div className={classes.dividerContainer}>
              <hr className={classes.border}/>
            </div>
          }
        </>
      )
    }
  }
  
  const renderContent = (props) => {
    return (
      <Popover
        {...props}
        className={classes.dropdownContainer}
      >
        <UCOutsideClickHandler
          onOutsideClick={() => {!(showDeleteModal || showEditModal) && handleCloseDropDown()}}
        >
          <div className={classes.inputContainer}>
            <input
              type="text"
              name="title"
              id="title"
              ref={inputRef}
              value={inputValue}
              maxLength={25}
              onChange={handleInputChange}
              autoComplete="off"
              placeholder={intl.formatMessage({id: 'guest.addLabel'})}
            />
          </div>
          <div className={classes.dividerContainer}>
            <hr className={classes.border}/>
          </div>
          {getLabelsContent()}
          {inputValue && inputValue.length > 2 &&
            <div
              className={classnames(classes.createNewContainer, disableCreate(inputValue) && classes.disabledCreateButton)}
              onClick={handleCreateLabel}
            >
              <AsLabelPlus/>
              <span className={classes.inlineText}>
                  <FormattedMessage id="guestLabel.createNew"/>:
                  <span className={classes.inputValueText}>
                  {inputValue}
                  </span>
                </span>
            </div>
          }
          <EditLabelModal
            showModal={showEditModal}
            setShowModal={setShowEditModal}
            setShowDeleteModal={setShowDeleteModal}
            label={labelToEdit}
            handleSubmit={updateLabel}
            successCallback={handleUpdateLabel}
          />
          
          <DeleteActionModal
            showModal={showDeleteModal}
            setShowModal={setShowDeleteModal}
            setSelectedRow={() => {
            }}
            modalIcon={<LabelIcon/>}
            modalTitle={<FormattedMessage id="guest.deleteLabel.title"/>}
            modalDescription={<FormattedMessage id="guest.deleteLabel.description"/>}
            submitCallback={handleDeleteLabel}
            cancelCallback={() => setShowEditModal(true)}
          />
        </UCOutsideClickHandler>
      </Popover>
    )
  }
  
  return (
    <div>
      <OverlayTrigger
        placement={placement}
        trigger="click"
        show={isOpen}
        overlay={(props) => renderContent(props)}
        rootClose
        flip
      >
        {renderSelectedLabel()}
      </OverlayTrigger>
    </div>
  );
};

export default LabelDropdown;
